import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import TextWithLabel from "../../../controls/TextWithLabel";
import ScannableBarcode from "../receivingBarcode/ScannableBarcode";
import { isDeveloperModeEnabled } from "../../../../shared/developerMode";
import Button from "../../../ui/Button";
import SlideOver from "../../../slideOver/SlideOver";
import LocationZoneStacked from "../../../packaging/LocationZoneStacked";
import PackagingBarcodeHistoryEventDetail from "./PackagingBarcodeHistoryEventDetail";
import {
  formatBarcodeSerial,
  formatHistoryContextSummary,
  formatHistoryDateTime,
  getHistoryActionBadgeClass,
  historyRowKey,
} from "./packagingBarcodeHistoryUtils";
import {
  formatAmountWithUnit,
  formatTraceabilityNumber,
} from "../../../../shared/traceabilityFormatting";

function LocationField({ row, className = "" }) {
  return (
    <LocationZoneStacked row={row} variant="field" className={className} />
  );
}

export default function PackagingBarcodeLookupResult({
  itemType,
  item,
  manufacturedProduct,
  histories,
  onTransfer,
  transferInProgress = false,
  canTransfer = false,
}) {
  const [slideOver, setSlideOver] = useState({
    open: false,
    childComponent: null,
  });

  const itemDateTime = formatHistoryDateTime(
    item?.created_at || item?.packaging_date,
  );
  const lastScanDateTime = formatHistoryDateTime(histories?.[0]?.created_at);
  const statusText =
    item?.status_label || (item?.status != null ? String(item.status) : "N/A");

  const rowsWithKeys = useMemo(
    () =>
      (histories || []).map((event, index) => ({
        ...event,
        _rowKey: historyRowKey(event, index),
      })),
    [histories],
  );

  useEffect(() => {
    setSlideOver({ open: false, childComponent: null });
  }, [item?.id, item?.barcode, histories]);

  const openEventDetail = (event) => {
    setSlideOver({
      open: true,
      childComponent: (
        <PackagingBarcodeHistoryEventDetail
          event={event}
          serialNumber={item?.serial_number}
          setOpen={setSlideOver}
        />
      ),
    });
  };

  const whenBody = (row) => {
    const dt = formatHistoryDateTime(row.created_at);
    return (
      <div>
        <p className="text-xs font-semibold text-gray-800">{dt.dateText}</p>
        {dt.timeText ? (
          <p className="text-xs text-gray-500">{dt.timeText}</p>
        ) : null}
      </div>
    );
  };

  const actionBody = (row) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getHistoryActionBadgeClass(row.action)}`}
    >
      {(row.action || "unknown").replace(/_/g, " ")}
    </span>
  );

  const typeBody = (row) => (
    <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
      {(row.type || "unknown").replace(/_/g, " ")}
    </span>
  );

  const reasonBody = (row) => {
    const text = row.reason || "—";
    const truncated = text.length > 72 ? `${text.slice(0, 69)}…` : text;
    return (
      <span
        className="text-gray-700"
        title={text.length > 72 ? text : undefined}
      >
        {truncated}
      </span>
    );
  };

  const contextBody = (row) => (
    <span className="text-gray-700">{formatHistoryContextSummary(row)}</span>
  );

  const detailsBody = (row) => (
    <Button
      type="button"
      variant="soft"
      size="sm"
      onClick={() => openEventDetail(row)}
    >
      Details
    </Button>
  );

  return (
    <div className="space-y-4 py-4">
      <div className="card-custom rounded-xl border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              Resolved Item
            </h2>
            <span className="rounded-full bg-main-purple/10 px-2.5 py-1 text-xs font-semibold uppercase text-main-purple">
              {itemType || "unknown"}
            </span>
          </div>
          {onTransfer ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onTransfer}
              disabled={!canTransfer || transferInProgress}
              loading={transferInProgress}
            >
              Transfer
            </Button>
          ) : null}
        </div>
        {!canTransfer ? (
          <p className="mt-2 text-xs text-gray-500">
            Transfer is only available for in-stock items.
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TextWithLabel label="ID" text={item?.id ?? "N/A"} />
          <TextWithLabel
            label="Barcode Serial"
            text={formatBarcodeSerial(item?.barcode || item?.shipping_barcode)}
          />
          <TextWithLabel label="Lot Number" text={item?.lot_number || "N/A"} />
          <TextWithLabel
            label="Created At"
            text={
              itemDateTime.timeText
                ? `${itemDateTime.dateText}, ${itemDateTime.timeText}`
                : itemDateTime.dateText
            }
          />
          {isDeveloperModeEnabled() ? (
            <div className="sm:col-span-2 lg:col-span-4">
              <p className="mb-1 block text-sm font-medium text-gray-600">
                Barcode
              </p>
              <ScannableBarcode
                value={item?.barcode || item?.shipping_barcode}
              />
            </div>
          ) : null}
          <TextWithLabel
            label="Product Name"
            text={item?.product_name || "N/A"}
          />
          <TextWithLabel
            label="Packing Size"
            text={formatAmountWithUnit(
              item?.packing_size,
              item?.unit_of_measure,
              item?.unit_of_measure_label,
            )}
          />
          <LocationField row={item} />
          <TextWithLabel label="Status" text={statusText} />
          <TextWithLabel
            label="Last Scan Date"
            text={
              lastScanDateTime.timeText
                ? `${lastScanDateTime.dateText}, ${lastScanDateTime.timeText}`
                : lastScanDateTime.dateText
            }
          />
          <TextWithLabel
            label="Order Item ID"
            text={item?.order_item_id != null ? item.order_item_id : "N/A"}
          />
        </div>

        <div className="mt-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Production Run
              </p>
              {manufacturedProduct?.id ? (
                <Link
                  className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                  to={`/manufactured-products/${manufacturedProduct.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </Link>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextWithLabel
                label="Manufactured Product"
                text={manufacturedProduct?.product_name || "N/A"}
              />
              <TextWithLabel
                label="Manufactured Lot"
                text={manufacturedProduct?.lot_number || "N/A"}
              />
              <TextWithLabel
                label="PLU"
                text={manufacturedProduct?.plu || "N/A"}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">History</h3>
          <span className="text-sm text-gray-500">
            {histories.length} event{histories.length === 1 ? "" : "s"}
          </span>
        </div>
        {histories.length === 0 ? (
          <div className="card-custom rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No history rows were returned for this barcode.
          </div>
        ) : (
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="card-custom overflow-hidden bg-white">
                  <DataTable
                    value={rowsWithKeys}
                    dataKey="_rowKey"
                    stripedRows
                    tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                    cellClassName="p-2 border-b border-gray-200 text-sm align-top"
                  >
                    <Column
                      body={whenBody}
                      header="When"
                      style={{ width: "8.5rem" }}
                    />
                    <Column
                      body={actionBody}
                      header="Action"
                      style={{ width: "9rem" }}
                    />
                    <Column
                      body={typeBody}
                      header="Type"
                      style={{ width: "7rem" }}
                    />
                    <Column
                      header="Operator"
                      body={(row) => row.operator?.label || "—"}
                      style={{ minWidth: "6rem" }}
                    />
                    <Column
                      header="Amount"
                      body={(row) =>
                        row.amount != null
                          ? formatTraceabilityNumber(row.amount)
                          : "—"
                      }
                      style={{ width: "5.5rem" }}
                    />
                    <Column
                      header="Reason"
                      body={reasonBody}
                      style={{ minWidth: "10rem", maxWidth: "14rem" }}
                    />
                    <Column
                      header="Context"
                      body={contextBody}
                      style={{ minWidth: "10rem", maxWidth: "12rem" }}
                    />
                    <Column
                      header=""
                      body={detailsBody}
                      style={{ width: "6rem" }}
                    />
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <SlideOver
        slideOver={slideOver}
        setSlideOver={setSlideOver}
        customClass="max-w-lg"
        childComponent={slideOver.childComponent}
      />
    </div>
  );
}
